/*
  Landing kit behaviour (about 3 KB).

  1. Slider: one slide visible at a time, arrows + dots, optional autoplay.
     Same behaviour as the Shogun slider it replaces (no transition).
  2. YouTube facade: the poster button is swapped for the real iframe on
     first click, so the YouTube script only loads when someone asks for it.
  3. Hosted video: a Shopify Files video sits behind a poster; the tap reveals
     the native player and starts it with sound. With the autoplay option the
     clip starts muted (browsers allow nothing else) and the tap turns the
     sound on; if the browser blocks even that, the poster comes back.
  4. Bundle cards: the stacked product cards deal themselves in the first
     time the band scrolls into view (IntersectionObserver).

  Clicks are delegated from the document; sliders and bundles are
  re-initialised on Shopify theme editor section loads. The guard makes a
  second execution a no-op.
*/
(function () {
  'use strict';

  if (window.lpLandingInit) return;
  window.lpLandingInit = true;

  /* ---- Slider ---- */

  function initSlider(root) {
    if (root.dataset.lpReady) return;
    root.dataset.lpReady = '1';

    var slides = Array.prototype.slice.call(root.querySelectorAll('.lp-slider__slide'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('.lp-slider__dot'));
    var prev = root.querySelector('.lp-slider__arrow--prev');
    var next = root.querySelector('.lp-slider__arrow--next');
    var index = 0;
    var timer = null;

    if (slides.length < 2) {
      root.classList.add('lp-slider--single');
      if (slides[0]) {
        slides[0].classList.add('is-active');
        slides[0].setAttribute('aria-hidden', 'false');
      }
      return;
    }

    function show(i) {
      index = (i + slides.length) % slides.length;
      slides.forEach(function (s, n) {
        s.classList.toggle('is-active', n === index);
        s.setAttribute('aria-hidden', n === index ? 'false' : 'true');
      });
      dots.forEach(function (d, n) {
        d.classList.toggle('is-active', n === index);
        d.setAttribute('aria-current', n === index ? 'true' : 'false');
      });
    }

    function restart() {
      if (!timer) return;
      clearInterval(timer);
      timer = setInterval(function () { show(index + 1); }, interval);
    }

    var interval = parseInt(root.dataset.lpInterval || '5', 10) * 1000;

    if (prev) prev.addEventListener('click', function () { show(index - 1); restart(); });
    if (next) next.addEventListener('click', function () { show(index + 1); restart(); });
    dots.forEach(function (d, n) {
      d.addEventListener('click', function () { show(n); restart(); });
    });

    // Swipe on touch devices.
    var startX = null;
    root.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
    root.addEventListener('touchend', function (e) {
      if (startX === null) return;
      var dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) show(dx < 0 ? index + 1 : index - 1);
      startX = null;
    }, { passive: true });

    if (root.dataset.lpAutoplay === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      timer = setInterval(function () { show(index + 1); }, interval);
      if (root.dataset.lpPause === 'true') {
        root.addEventListener('mouseenter', function () { clearInterval(timer); });
        root.addEventListener('mouseleave', function () { restart(); });
      }
    }

    show(0);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-lp-slider]').forEach(initSlider);
    (scope || document).querySelectorAll('[data-lp-bundle]').forEach(initBundle);
    (scope || document).querySelectorAll('[data-lp-hv]').forEach(initHostedVideo);
  }

  /* ---- Hosted video with autoplay: muted start, poster back if blocked ---- */

  function initHostedVideo(wrapper) {
    if (wrapper.dataset.lpHvReady) return;
    wrapper.dataset.lpHvReady = '1';
    if (!wrapper.classList.contains('lp-hv--auto')) return;

    var video = wrapper.querySelector('video');
    if (!video) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      video.removeAttribute('autoplay');
      video.pause();
      wrapper.classList.add('lp-hv--auto-off');
      return;
    }

    video.muted = true;
    var playing = video.play();
    if (playing && playing.catch) {
      playing.catch(function () { wrapper.classList.add('lp-hv--auto-off'); });
    }
  }

  /* ---- Bundle: deal the cards in when the band scrolls into view ---- */

  function initBundle(root) {
    if (root.dataset.lpBundleReady) return;
    root.dataset.lpBundleReady = '1';

    var cards = root.querySelectorAll('.lp-bundle__card').length;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!cards || reduce || !('IntersectionObserver' in window)) {
      root.classList.add('is-inview', 'is-settled');
      return;
    }

    // The section's inline script usually armed this already; the editor's
    // section re-render does not run inline scripts, so arm it here too.
    root.classList.add('lp-bundle--armed');

    var stage = root.querySelector('[data-lp-bundle-stage]') || root;
    var io = new IntersectionObserver(function (entries) {
      if (!entries.some(function (e) { return e.isIntersecting; })) return;
      io.disconnect();
      root.classList.add('is-inview');
      setTimeout(function () { root.classList.add('is-settled'); }, cards * 110 + 750);
    }, { threshold: 0.2 });
    io.observe(stage);
  }

  /* ---- YouTube facade ---- */

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-lp-yt-play]');
    if (!trigger) return;

    var wrapper = trigger.closest('[data-lp-yt]');
    if (!wrapper) return;

    var iframe = document.createElement('iframe');
    iframe.setAttribute('src', wrapper.getAttribute('data-src'));
    iframe.setAttribute('title', wrapper.getAttribute('data-title') || 'Video player');
    iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
    iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('frameborder', '0');

    trigger.remove();
    wrapper.appendChild(iframe);
    iframe.focus();
  });

  /* ---- Shopify-hosted video: tap the poster, play with sound ---- */

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-lp-hv-play]');
    if (!trigger) return;

    var wrapper = trigger.closest('[data-lp-hv]');
    var video = wrapper && wrapper.querySelector('video');
    if (!video) return;

    wrapper.classList.add('is-playing');
    video.controls = true;
    video.muted = false;
    // A muted autoplay clip restarts so the viewer hears it from the top.
    if (wrapper.classList.contains('lp-hv--auto') && !wrapper.classList.contains('lp-hv--auto-off')) {
      try { video.currentTime = 0; } catch (e) { /* not seekable yet */ }
    }
    var playing = video.play();
    if (playing && playing.catch) playing.catch(function () {});
    video.focus();
  });

  /* ---- Boot ---- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  // Theme editor: bring the selected bundle card to the front while editing it.
  document.addEventListener('shopify:block:select', function (event) {
    var card = event.target && event.target.closest && event.target.closest('.lp-bundle__card');
    if (card) card.classList.add('is-selected');
  });
  document.addEventListener('shopify:block:deselect', function (event) {
    var card = event.target && event.target.closest && event.target.closest('.lp-bundle__card');
    if (card) card.classList.remove('is-selected');
  });
})();
